import React from 'react';
import { defineMessages, FormattedMessage, FormattedNumber } from 'react-intl';
import PropTypes from 'prop-types';

import { DefaultIntlFormatValues, EqualWidthMessageColumns } from '@ratehub/base-ui';

import MESSAGES from '../definitions/Messages';
import FooterDefaultIntlFormatValues from '../definitions/FooterDefaultIntlFormatValues';
import CardFeaturesTable from '../components/CardFeaturesTable';
import isARewardsCard from './isARewardsCard';
import isCIBCCreditCard from './isCIBCCreditCard';


function getCardFooterTabContent({ cardInfo, includeRatehubReview, footerTabNames }) {
    const {
        name,
        bonusDescriptionMarkup,
        ratehubReview,
        minimumCreditScore,
        minimumIncome,
        purchaseInterest,
        cashAdvanceInterest,
        balanceTransferInterest,
        balanceTransferPromoInterest,
        balanceTransferPromoMonths,
        annualFee,
        isFirstYearFeeWaived,
        featureDescriptions,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);
    const isRewardsCard = isARewardsCard(cardInfo.displayType);
    const willDisplayCardFeaturesTable = !!featureDescriptions.length || isRewardsCard;

    const cardFooterTabContent = [
        {
            key: footerTabNames.perks,
            content: (
                <div
                    className="rh-mx-1_5 rh-py-1"
                    data-test-name="footer-perks"
                >
                    <If condition={willDisplayCardFeaturesTable}>
                        <h3 className="rh-title-3xs">
                            <FormattedMessage
                                {...LOCAL_MESSAGES.featureDescriptionIntro}
                                values={{ name }}
                            />
                        </h3>

                        <div className="card-features-table-wrapper">
                            <CardFeaturesTable
                                featureDescriptions={featureDescriptions}
                                isShowingAllFeatures={isRewardsCard}
                            />
                        </div>
                    </If>

                    <If condition={bonusDescriptionMarkup}>
                        <h3 className="rh-title-3xs">
                            <FormattedMessage
                                {...LOCAL_MESSAGES.otherPerks}
                                // for Rewards, we always show CardFeaturesTable, so this header will always be 'Other perks'
                                values={{ isOther: willDisplayCardFeaturesTable }}
                            />
                        </h3>

                        <div
                            className="bonus-description-markup rh-text-xs leading-l"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: bonusDescriptionMarkup,
                            }}
                        />
                    </If>
                </div>
            ),
        },

        {
            key: footerTabNames.details,
            content: (
                <DetailsContent
                    minimumCreditScore={minimumCreditScore}
                    minimumIncome={minimumIncome}
                    purchaseInterest={purchaseInterest}
                    cashAdvanceInterest={cashAdvanceInterest}
                    balanceTransferInterest={balanceTransferInterest}
                    balanceTransferPromoInterest={balanceTransferPromoInterest}
                    balanceTransferPromoMonths={balanceTransferPromoMonths}
                    annualFee={annualFee}
                    isFirstYearFeeWaived={isFirstYearFeeWaived}
                    isCIBC={isCIBC}
                />
            ),
        },
    ];

    // since the tab content structure relies on the `key` property, we can put the second tab anywhere
    if (includeRatehubReview) {
        cardFooterTabContent.push({
            key: footerTabNames.prosAndCons,
            content: (
                <div
                    className="rh-mx-1_5 rh-py-1"
                    data-test-name="footer-pros-and-cons"
                >
                    <h3 className="rh-title-3xs">
                        <FormattedMessage
                            id="edb.credit-card.ratehubs-take"
                            defaultMessage="Ratehub’s take"
                        />
                    </h3>

                    <div
                        className="ratehub-review-markup rh-text-xs leading-l"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: ratehubReview?.content,
                        }}
                    />
                </div>
            ),
        });
    }

    return cardFooterTabContent;
}


function DetailsContent({
    minimumCreditScore,
    minimumIncome,
    purchaseInterest,
    cashAdvanceInterest,
    balanceTransferInterest,
    balanceTransferPromoInterest,
    balanceTransferPromoMonths,
    annualFee,
    isFirstYearFeeWaived,
    isCIBC,
}) {
    // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
    const balanceTransferRate = balanceTransferInterest != null
        ? (
            <FormattedNumber
                value={balanceTransferInterest / 100}
                format="percent2"
            />
        )
        : notApplicable;    // really shouldn’t expect this, but consider it a safeguard

    const columns = [
        (
            <>
                <h3 className="rh-title-3xs rh-mb-0_5">
                    <FormattedMessage
                        id="edb.credit-card.details.eligibility"
                        defaultMessage="Eligibility requirements"
                    />
                </h3>
                <If condition={minimumCreditScore}>
                    <p
                        className="rh-text-xs rh-mb-0_25"
                        data-test-name="minimum-credit-score"
                    >
                        <FormattedMessage
                            id="edb.credit-card.details.credit-score"
                            defaultMessage="Minimum credit score: <strong>{minimumCreditScore}</strong>"
                            values={{
                                ...DefaultIntlFormatValues,
                                minimumCreditScore,
                            }}
                        />
                    </p>
                </If>
                <p className="rh-text-xs rh-m-0">
                    <FormattedMessage
                        id="edb.credit-card.details.minimum-income"
                        defaultMessage={`Minimum income: <b>{minimumIncome, select,
                                        0 {{notApplicable}}
                                        other {{minimumIncome, number, CAD0}/yr}}</b>`}
                        values={{
                            ...DefaultIntlFormatValues,
                            minimumIncome,
                            notApplicable,
                        }}
                    />
                </p>
            </>
        ),

        (
            <>
                <h3 className="rh-title-3xs rh-mb-0_5">
                    <FormattedMessage
                        id="edb.credit-card.details.interest-rates"
                        defaultMessage="Interest rates"
                        key="interest-rates"
                    />
                </h3>

                {/* always show ALL THREE interest rates, accepting null as N/A (is that you Amex?) */}
                <p className="rh-text-xs rh-mt-0_25 rh-mb-0">
                    <FormattedMessage
                        id="edb.credit-card.details.details.purchase-interest"
                        defaultMessage="Purchase interest: "
                    />
                    <strong data-test-name="purchase-interest-rate">
                        <If condition={purchaseInterest != null}>
                            <FormattedNumber
                                value={purchaseInterest / 100}
                                format="percent2"
                            />
                        </If>
                        <If condition={purchaseInterest == null}>
                            {notApplicable}
                        </If>
                    </strong>
                </p>

                <p className="rh-text-xs rh-mt-0_25 rh-mb-0">
                    <FormattedMessage
                        id="edb.credit-card.details.details.cash-advance"
                        defaultMessage="Cash advance: "
                    />
                    <strong data-test-name="cash-advance-interest-rate">
                        <If condition={cashAdvanceInterest != null}>
                            <FormattedNumber
                                value={cashAdvanceInterest / 100}
                                format="percent2"
                            />
                        </If>
                        <If condition={cashAdvanceInterest == null}>
                            {notApplicable}
                        </If>
                    </strong>
                </p>

                <p
                    className="rh-text-xs rh-mt-0_25 rh-mb-0"
                    data-test-name="balance-transfer-interest-rate"
                >
                    <FormattedMessage
                        id="edb.credit-card.details.details.cibc.balance-transfer"
                        defaultMessage={
                            `
                            Balance transfer: {balanceTransferPromoInterest, select,
                                   undefined {<strong>{balanceTransferRate}</strong>}
                                   other {
                                       <strong>{balanceTransferPromoInterest, number, percent2}</strong>
                                       {balanceTransferPromoMonths, select,
                                           null {}
                                           undefined {}
                                           other {
                                               for the first {balanceTransferPromoMonths, plural, one {month} other {# months}}.
                                               <strong>{balanceTransferRate}</strong> after promo.
                                           }
                                       }
                                   }
                               }
                            `
                        }
                        values={{
                            balanceTransferPromoInterest: balanceTransferPromoInterest == null  // check for null or undefined
                                ? undefined
                                : balanceTransferPromoInterest / 100,
                            balanceTransferPromoMonths,
                            balanceTransferRate: balanceTransferRate,
                            ...DefaultIntlFormatValues,
                        }}
                    />
                </p>
            </>
        ),
    ];

    if (isCIBC) {
        columns.push((
            <>
                <h3 className="rh-title-3xs rh-mb-0_5">
                    <FormattedMessage
                        id="edb.credit-card.details.cibc-annual-fee"
                        defaultMessage="Annual fee"
                        key="annual-fee"
                    />
                </h3>
                <p className="rh-text-xs rh-mt-0_25 rh-mb-0 weight-medium">
                    <FormattedMessage
                        {...MESSAGES.annualFeeSummary}
                        values={{
                            annualFee,
                            isFirstYearFeeWaived,
                            zero: 0,
                            ...FooterDefaultIntlFormatValues,
                            ...DefaultIntlFormatValues,
                        }}
                    />
                </p>
            </>
        ));
    }

    return (
        <EqualWidthMessageColumns
            className="rh-py-1 rh-mx-1_5"
            data-test-name="footer-details"
            columns={columns}
        />
    );
}

DetailsContent.propTypes = {
    minimumCreditScore: PropTypes.number,
    minimumIncome: PropTypes.number.isRequired,
    purchaseInterest: PropTypes.number,
    cashAdvanceInterest: PropTypes.number,
    balanceTransferInterest: PropTypes.number,
    balanceTransferPromoInterest: PropTypes.number,
    balanceTransferPromoMonths: PropTypes.number,
    annualFee: PropTypes.number.isRequired,
    isFirstYearFeeWaived: PropTypes.bool.isRequired,
    isCIBC: PropTypes.bool,
};

DetailsContent.defaultProps = {
    minimumCreditScore: null,
    purchaseInterest: null,
    cashAdvanceInterest: undefined,
    balanceTransferInterest: undefined,
    balanceTransferPromoInterest: undefined,
    balanceTransferPromoMonths: undefined,
    isCIBC: false,
};


const LOCAL_MESSAGES = defineMessages({
    otherPerks: {
        id: 'edb.credit-card.footer.perks.subtitle',
        defaultMessage: '{isOther, select, true {Other perks} other {Perks}}',
    },

    featureDescriptionIntro: {
        id: 'edb.credit-card.footer.feature-description-intro',
        defaultMessage: 'Perks of the {name}',
    },
});

// this seems like an odd way to treat an undefined interest rate; what’s up Amex?
// at least it doesn’t need to be translated
const notApplicable = 'N/A';


export default getCardFooterTabContent;
